import { useState } from "react";
import { Link } from "react-router-dom";
import { api, ApiError, type Submission } from "../api";
import { DeclineDialog } from "./DeclineDialog";
import { FocusChips } from "./FocusChips";
import { STATUS_LABEL, bucketLength, relative } from "../format";

/** Accept and decline for a list of submissions, with the one decline dialog and the one error line. */
export function useRespond() {
    const [declining, setDeclining] = useState<Submission | null>(null);
    const [busy, setBusy] = useState<{ id: string; action: "accept" | "decline" } | null>(null);
    const [actionError, setActionError] = useState<string | null>(null);

    const respond = async (s: Submission, action: "accept" | "decline", message?: string) => {
        setBusy({ id: s.id, action }); setActionError(null);
        try {
            await api.respond(s.id, action, message);
            setDeclining(null);
        } catch (err) {
            setActionError(err instanceof ApiError ? err.message : "That did not go through. Check your connection and try again.");
        } finally {
            setBusy(null);
        }
    };

    const dialog = declining ? (
        <DeclineDialog playerName={declining.playerDisplayName} busy={busy?.id === declining.id}
            onCancel={() => setDeclining(null)} onConfirm={(message) => void respond(declining, "decline", message)} />
    ) : null;

    return { busyId: busy?.id ?? null, accepting: busy?.action === "accept" ? busy.id : null, actionError, dialog, accept: (s: Submission) => void respond(s, "accept"), decline: setDeclining };
}

export function SubmissionCard({ s, busy, accepting = false, onAccept, onDecline }: { s: Submission; busy: boolean; accepting?: boolean; onAccept: () => void; onDecline: () => void }) {
    const open = s.status === "submitted";
    const clipNote = s.cut.status === "pending" ? `Preparing clips ${s.cut.readyCount}/${s.clipCount}` : s.cut.status === "failed" ? "Clips failed" : null;
    const deadline = s.status === "submitted" ? `Answer ${relative(s.timing.respondByAt)}`
        : s.status === "accepted" || s.status === "in_review" ? `Due ${relative(s.timing.dueAt)}`
            : s.status === "returned" ? `Returned ${relative(s.timing.returnedAt)}` : `Closed ${relative(s.timing.closedAt)}`;
    return (
        <li className={`card row status-${s.status}`}>
            <div className="card-main">
                <div className="card-title">
                    <strong>{s.playerDisplayName}</strong>
                    <span className={`pill ${s.status}`}>{STATUS_LABEL[s.status]}</span>
                    {clipNote && <span className="pill warn">{clipNote}</span>}
                </div>
                <div className="muted small">{s.clubName} · {bucketLength(s.clipCount, s.totalSec)} · sent {relative(s.timing.submittedAt)} · {deadline}</div>
                <FocusChips areas={s.focusAreas} />
                {s.note && <p className="note">“{s.note}”</p>}
            </div>
            <div className="card-actions">
                {open ? (
                    <>
                        <button className="btn ok" disabled={busy || s.cut.status === "failed"} onClick={onAccept} aria-busy={accepting}>
                            {accepting ? <><span className="spinner small on-light" aria-hidden /> Accepting</> : "Accept"}
                        </button>
                        <button className="btn decline" disabled={busy} onClick={onDecline}>Decline</button>
                    </>
                ) : null}
                {(s.status === "accepted" || s.status === "in_review") && <Link className="btn primary" to={`/review/${s.id}`}>Open review</Link>}
                {s.status === "returned" && <Link className="btn" to={`/review/${s.id}`}>View</Link>}
                {open && <Link className="btn ghost small" to={`/review/${s.id}`}>Preview clips</Link>}
            </div>
        </li>
    );
}
